import { fg } from '@atlassian/jira-feature-gating';
import type { fnGetFilteredProductLinks } from '@atlassian/global-shortcuts';
import { GLOBAL_APP_MENU_ID_TO_APS_KEY } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type GlobalAppMenuId,
	type JiraConfigurableNavigationItem,
	type MenuItemConfigData,
} from '../../types.tsx';
import {
	hasGlobalAppLicense,
	isGlobalAppMenuId,
	isSupportedMenuId,
	type GlobalAppsLicense,
} from '../global-apps/index.tsx';

/**
 * Gets all the ConfigurableMenuIds that are NOT global apps for platform ingestion.
 * MenuIds and GlobalAppIdsare split because \@atlassian/customization-modal and
 * \@atlassian/global-more-menu take in separate props for apps/product items.
 */
export const getProductItemsData = (
	navItems: JiraConfigurableNavigationItem[],
): MenuItemConfigData[] =>
	fg('nav4_global_links_jira')
		? navItems
				.filter((navItem) => isSupportedMenuId(navItem.menuId))
				.filter((navItem) => !isGlobalAppMenuId(navItem.menuId))
				.filter((navItem) => {
					if (!fg('nav4_gas_include_teams')) {
						return true;
					}
					return navItem.menuId !== L1_MENU_ID.TEAMS;
				})
				.map((item) => {
					return {
						menuId: item.menuId,
						visible: item.isVisible,
					};
				})
		: navItems
				.filter((navItem) => !isGlobalAppMenuId(navItem.menuId))
				.map((item) => {
					return {
						menuId: item.menuId,
						visible: item.isVisible,
					};
				});

/**
 * Gets only GlobalAppMenuIds from ConfigurableMenuIds for platform ingestion.
 * MenuIds and GlobalAppIds are split because \@atlassian/customization-modal and
 * \@atlassian/global-more-menu take in separate props for apps/product items.
 */
export const getAppItemsData = (
	navItems: JiraConfigurableNavigationItem[],
	globalAppLicense: GlobalAppsLicense,
	getFilteredProductLinks?: fnGetFilteredProductLinks,
): MenuItemConfigData[] =>
	navItems
		.filter((navItem) => isGlobalAppMenuId(navItem.menuId))
		.filter((navItem) => {
			// TODO: put this under a feature gate
			if (!fg('nav4_global_links_jira')) {
				return true;
			}
			if (getFilteredProductLinks) {
				if (navItem.menuId in GLOBAL_APP_MENU_ID_TO_APS_KEY) {
					const apsKey = GLOBAL_APP_MENU_ID_TO_APS_KEY[navItem.menuId];
					const filteredProducts = getFilteredProductLinks(apsKey);
					if (filteredProducts.links.length === 0) {
						// If there are no filtered links, we don't want to show this item.
						return false;
					}
				}
			}
			return true;
		})
		.filter((navItem) => hasGlobalAppLicense(navItem.menuId, globalAppLicense))
		.map((item) => {
			return {
				menuId: item.menuId,
				visible: item.isVisible,
			};
		});

/**
 * Returns all the currently visible global apps.
 */
export const getVisibleAppItemsData = (
	navItems: JiraConfigurableNavigationItem[],
	globalAppLicense: GlobalAppsLicense,
	getFilteredProductLinks?: fnGetFilteredProductLinks,
): GlobalAppMenuId[] => {
	return navItems
		.filter((navItem) => navItem.isVisible)
		.map(({ menuId }) => menuId)
		.filter(isGlobalAppMenuId)
		.filter((navItem) => {
			if (!fg('nav4_global_links_jira')) {
				return true;
			}
			if (getFilteredProductLinks) {
				if (navItem in GLOBAL_APP_MENU_ID_TO_APS_KEY) {
					const apsKey = GLOBAL_APP_MENU_ID_TO_APS_KEY[navItem];
					const filteredProducts = getFilteredProductLinks(apsKey);
					if (filteredProducts.links.length === 0) {
						// If there are no filtered links, we don't want to show this item.
						return false;
					}
				}
			}
			return true;
		})
		.filter((menuId) => hasGlobalAppLicense(menuId, globalAppLicense));
};
